// src/components/SettingsForm.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { FaRegSave, FaTimes, FaPalette } from 'react-icons/fa';
import ThemeEditorModal from './ThemeEditorModal.jsx';
import './SettingsForm.css';

const API = import.meta.env.VITE_API_URL;

function getLS(k, d) { try { const v = localStorage.getItem(k); return v ?? d; } catch { return d; } }

export default function SettingsForm({ onSaved, onClose, asModal = false }) {
  // Feature toggle: hide theme editor UI for now
  const ENABLE_THEME_EDITOR = false;
  const user = useMemo(() => JSON.parse(localStorage.getItem('user')) || {}, []);
  const [form, setForm] = useState({
    fullname: '',
    password: '',
    sessiontimeout: 30,
    email: '',
    cell: '',
    theme: user.theme || 'orangish',
    eordersRefreshSec: Number(getLS('eorders.refreshIntervalSec', '0')) || 0,
  });
  const [availableThemes, setAvailableThemes] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [themeOpen, setThemeOpen] = useState(false);

  const applyTheme = (themeData) => {
    const defaults = {
      primary_color: '#ff6200',
      primary_hover: '#ff8c00',
      background_dark: '#1a1a1a',
      card_background: '#2c2c2c',
      input_background: '#3a3a3a',
      text_color: '#ffffff',
      error_color: '#ff0000',
      success_color: '#00ff00',
    };
    const theme = { ...defaults, ...themeData };
    document.documentElement.style.setProperty('--primary-color', theme.primary_color);
    document.documentElement.style.setProperty('--primary-hover', theme.primary_hover);
    document.documentElement.style.setProperty('--background-dark', theme.background_dark);
    document.documentElement.style.setProperty('--card-background', theme.card_background);
    document.documentElement.style.setProperty('--input-background', theme.input_background);
    document.documentElement.style.setProperty('--text-color', theme.text_color);
    document.documentElement.style.setProperty('--error-color', theme.error_color);
    document.documentElement.style.setProperty('--success-color', theme.success_color);
  };

  useEffect(() => {
    const fetchUser = async () => {
      if (!user.userid) return;
      setIsLoading(true);
      try {
        const response = await fetch(`${API}/api/settings`, { headers: { userid: user.userid } });
        const data = await response.json();
        if (response.ok) {
          setForm((prev) => ({
            ...prev,
            fullname: data.fullname || '',
            password: '',
            sessiontimeout: data.sessiontimeout || 30,
            email: data.email || '',
            cell: data.cell || '',
            theme: data.theme || 'orangish',
            eordersRefreshSec: Number(data.refreshtimeout || 0),
          }));
          setAvailableThemes(data.availableThemes || []);
          applyTheme(data.themeData || {});
          try {
            // Stash in local user for other screens to discover
            const u = JSON.parse(localStorage.getItem('user') || '{}');
            localStorage.setItem('user', JSON.stringify({ ...u, refreshtimeout: Number(data.refreshtimeout || 0) }));
          } catch {}
        } else {
          setError(data.message || 'Failed to fetch user settings');
          applyTheme({});
        }
      } catch (e) {
        setError('Error fetching user settings');
        applyTheme({});
      } finally {
        setIsLoading(false);
      }
    };
    fetchUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.userid]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Sanitize numeric fields and clamp to allowed ranges
    if (name === 'sessiontimeout') {
      const digits = String(value || '').replace(/\D+/g, '');
      let n = digits === '' ? '' : Math.min(60, Math.max(0, parseInt(digits, 10)));
      setForm((prev) => ({ ...prev, sessiontimeout: n }));
      return;
    }
    if (name === 'eordersRefreshSec') {
      const digits = String(value || '').replace(/\D+/g, '');
      let n = digits === '' ? '' : Math.min(5, Math.max(0, parseInt(digits, 10)));
      setForm((prev) => ({ ...prev, eordersRefreshSec: n }));
      return;
    }

    setForm((prev) => ({ ...prev, [name]: value }));
    if (name === 'theme') {
      const selectedTheme = availableThemes.find((t) => t.name === value);
      if (selectedTheme) {
        applyTheme(selectedTheme);
        setError('');
      } else {
        setError('Selected theme is not available');
      }
    }
  };

  const persistLocalSettings = () => {
    // UI field is in MINUTES; convert to seconds for timers
    const minutes = Math.max(0, Number(form.eordersRefreshSec || 0));
    const sec = minutes * 60;
    try { localStorage.setItem('eorders.refreshIntervalSec', String(sec)); } catch {}
    // Inform listeners (e.g., EOrders.jsx) to reconfigure timers in SECONDS
    try { window.dispatchEvent(new CustomEvent('eorders:refreshIntervalChanged', { detail: { seconds: sec } })); } catch {}
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    setIsLoading(true);
    setError('');
    setSuccess('');
    try {
      // Persist local-only settings first
      persistLocalSettings();

      // Persist server-side settings currently supported (theme + timeouts)
      const response = await fetch(`${API}/api/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', userid: user.userid },
        body: JSON.stringify({
          fullname: form.fullname?.trim() || '',
          email: form.email || '',
          cell: form.cell || '',
          theme: form.theme,
          sessiontimeout: Math.max(0, Math.min(60, Number(form.sessiontimeout || 0))),
          refreshtimeout: Math.max(0, Number(form.eordersRefreshSec || 0)),
        }),
      });
      const data = await response.json();
      setIsLoading(false);
      if (response.ok) {
        setSuccess('Settings saved');
        try {
          const rt = Number(((data.refreshtimeout ?? form.eordersRefreshSec) || 0));
          localStorage.setItem(
            'user',
            JSON.stringify({
              ...user,
              theme: data.theme,
              refreshtimeout: rt,
              fullname: form.fullname?.trim() || user.fullname,
              email: form.email || user.email,
              cell: form.cell || user.cell,
            })
          );
        } catch {}
        if (data.themeData) {
          try { localStorage.setItem('themeData', JSON.stringify(data.themeData)); } catch {}
          applyTheme(data.themeData);
        }
        if (onSaved) onSaved();
      } else {
        setError(data.message || 'Failed to update settings');
        const originalTheme = availableThemes.find((t) => t.name === user.theme) || {};
        applyTheme(originalTheme);
      }
    } catch (err) {
      setIsLoading(false);
      setError('Error updating settings: ' + err.message);
    }
  };

  return (
    <form onSubmit={handleSubmit} className={asModal ? 'settings-form modal-body-form' : 'settings-form'}>
      {error && <span className="error-message">{error}</span>}
      {success && <span className="success-message">{success}</span>}
      {isLoading && (
        <div className="loading-modal">
          <div className="loading-content">
            <span className="loading-icon"></span>
            <span>Saving your settings... please wait</span>
          </div>
        </div>
      )}

      <div className="input-group">
        <label htmlFor="fullname">Full Name</label>
        <input type="text" id="fullname" name="fullname" value={form.fullname} onChange={handleInputChange} className="input" disabled={isLoading} />
      </div>

      <div className="input-group">
        <label htmlFor="email">Email</label>
        <input type="email" id="email" name="email" value={form.email} onChange={handleInputChange} className="input" disabled={isLoading} />
      </div>

      <div className="input-group">
        <label htmlFor="sessiontimeout">Session Timeout (minutes)</label>
        <input
          type="text"
          id="sessiontimeout"
          name="sessiontimeout"
          value={form.sessiontimeout}
          onChange={handleInputChange}
          inputMode="numeric"
          maxLength={2}
          placeholder="0–60"
          className="input input-num-small"
          disabled={isLoading}
        />
      </div>

      <div className="input-group">
        <label htmlFor="theme">Theme</label>
        <select id="theme" name="theme" value={form.theme} onChange={handleInputChange} className="input" disabled={isLoading}>
          {availableThemes.map((theme) => (
            <option key={theme.name} value={theme.name}>{theme.name.charAt(0).toUpperCase() + theme.name.slice(1)}</option>
          ))}
        </select>
        {ENABLE_THEME_EDITOR && (
          <div style={{ marginTop: 8 }}>
            <button type="button" className="btn" onClick={() => setThemeOpen(true)} disabled={isLoading}>
              <FaPalette style={{ marginRight: 6 }} /> Edit Theme
            </button>
          </div>
        )}
      </div>

      <div className="input-group">
        <label htmlFor="eordersRefreshSec">E-Orders Auto-Refresh ( minutes )</label>
        <input
          type="text"
          id="eordersRefreshSec"
          name="eordersRefreshSec"
          value={form.eordersRefreshSec}
          onChange={handleInputChange}
          inputMode="numeric"
          maxLength={2}
          placeholder="0–5 (0 = off)"
          className="input input-num-small"
          disabled={isLoading}
        />
        <small className="hint">0 disables auto-refresh</small>
      </div>

      <div className={asModal ? 'modal-footer' : ''} style={{ display:'flex', gap:8, justifyContent: 'flex-end', marginTop: 12 }}>
        {asModal && (
          <button type="button" className="btn" onClick={onClose} disabled={isLoading}>
            <FaTimes style={{ marginRight: 6 }} /> Close
          </button>
        )}
        <button type="submit" className="btn primary" disabled={isLoading}>
          <FaRegSave style={{ marginRight: 6 }} /> Save Settings
        </button>
      </div>

      {ENABLE_THEME_EDITOR && (
        <ThemeEditorModal
          open={themeOpen}
          onClose={() => setThemeOpen(false)}
          onSaved={() => setThemeOpen(false)}
        />
      )}
    </form>
  );
}
